import {
  Ellipsis,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { fileShape } from "components/Modal/ChooseFilesModal/propTypes";
import FilesTable from "components/Modal/UploadFilesModal/FilesTable";
import { isUploading } from "components/Modal/UploadFilesModal/utils";
import { UPLOAD_ENTITY_TYPES } from "constants/documentUploads";
import { modalTypes } from "constants/types";
import useUploadUnloadGuard from "hooks/useUploadUnloadGuard";
import { arrayOf, func, shape, string } from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hideModal } from "slices/app/actions/modals";
import { resetState } from "slices/workspace/chooseFilesModalSlice";
import {
  clearFileUploadStates,
  setCurrentFlowId,
  setEventSource,
} from "slices/workspace/foldersSlice";
import { selectFolders } from "slices/workspace/selectors";

import { selectSelectedCollection } from "../../../slices/collections/selectors";

const UploadFilesModal = ({ target, onFilesUploaded, displayedFiles }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const collection = useSelector(selectSelectedCollection);

  useUploadUnloadGuard();

  const header = useMemo(() => {
    if (target === UPLOAD_ENTITY_TYPES.COLLECTIONS) {
      return `Collection: ${collection?.name}`;
    } else if (target === UPLOAD_ENTITY_TYPES.DOC_STYLE_TEMPLATES) {
      return "Uploading Document Template(s)";
    } else {
      return "Analyzing Documents";
    }
  }, [collection, target]);

  const description = useMemo(() => {
    if (target === UPLOAD_ENTITY_TYPES.COLLECTIONS) {
      return "Indexing in progress";
    } else if (target === UPLOAD_ENTITY_TYPES.DOC_STYLE_TEMPLATES) {
      return "We are currently uploading the document templates";
    } else {
      return "We are currently uploading and analyzing the documents";
    }
  }, [target]);

  const { fileUploadStates, folderId, eventSource } =
    useSelector(selectFolders);
  const [uploading, setUploading] = useState(false);

  const closeModal = () => {
    dispatch(clearFileUploadStates());
    dispatch(resetState());
    dispatch(hideModal(modalTypes.UPLOAD_FILES));
  };

  const handleClose = () => {
    if (target === UPLOAD_ENTITY_TYPES.COLLECTIONS) {
      onFilesUploaded(collection);
    } else {
      onFilesUploaded(fileUploadStates, dispatch, navigate, folderId);
    }
    closeModal();
  };

  // Disable Close button while uploading
  useEffect(() => {
    setUploading(isUploading(fileUploadStates));
  }, [fileUploadStates]);

  // Abort SSE connection on modal unmount
  useEffect(() => {
    return () => {
      if (eventSource && !eventSource.signal.aborted) {
        eventSource.abort();
        dispatch(setEventSource(null));
      }
      dispatch(setCurrentFlowId(null));
    };
  }, [eventSource, dispatch]);

  return (
    <Modal
      className="upload_files_modal"
      onDismiss={handleClose}
      restrictWidth={false}
      backdrop
      show
    >
      <Modal.Header>
        <Ellipsis text={header} />
      </Modal.Header>
      <Modal.Body>
        <div className="upload_files_modal__description">{description}</div>
        <div className="upload_files_modal__table_wrapper">
          <FilesTable files={displayedFiles} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        {uploading && (
          <span className="upload_files_modal__uploading">Uploading</span>
        )}
        <NewButton
          variant={NewButtonVariant.l1}
          disabled={uploading}
          onClick={handleClose}
        >
          Close
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

UploadFilesModal.propTypes = {
  // Upload target (folder, opportunity ...)
  target: string.isRequired,
  onFilesUploaded: func,
  displayedFiles: arrayOf(shape(fileShape)),
};

export default UploadFilesModal;
